// src/pages/creator-panel/CallLogs/tabs/AnalysisTab.js
// Read-only display of Gemini-generated summary + outcome + extracted data.
import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import JsonViewer from '../components/JsonViewer';

const SectionHeading = ({ children }) => (
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 1, mt: 3 }}>
        {children}
    </Typography>
);

const SuccessPill = ({ value }) => {
    const v = String(value || '').toLowerCase();
    const color = v === 'true' ? 'success' : v === 'false' ? 'error' : 'default';
    return (
        <Chip
            label={String(value || '—')}
            color={color}
            size="small"
            sx={{ fontWeight: 600 }}
        />
    );
};

const tryParse = (text) => {
    if (!text || typeof text !== 'string') return null;
    try { return JSON.parse(text); } catch (e) { return null; }
};

const AnalysisTab = ({ session }) => {
    const summary = session?.summary;
    const success = session?.success_evaluation;
    const data = session?.extracted_data;
    const metadata = session?.metadata;
    const overrides = session?.assistant_overrides;

    return (
        <Box sx={{ pb: 2 }}>
            <SectionHeading>Summary</SectionHeading>
            {summary ? (
                <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.7 }}>
                    {summary}
                </Typography>
            ) : (
                <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                    No summary available for this call.
                </Typography>
            )}

            <SectionHeading>Success Evaluation</SectionHeading>
            <SuccessPill value={success} />

            <SectionHeading>Data</SectionHeading>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                This is the data extracted based on the Conversation.
            </Typography>
            <Box sx={{
                bgcolor: 'action.hover',
                border: '1px solid', borderColor: 'divider',
                borderRadius: 1, p: 1.5,
                fontFamily: 'monospace', fontSize: 13, color: 'text.primary',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
                {(() => {
                    if (!data || data === 'N/A') return '"N/A"';
                    const parsed = tryParse(data);
                    if (parsed) return JSON.stringify(parsed, null, 2);
                    return data;
                })()}
            </Box>

            <SectionHeading>Metadata</SectionHeading>
            {metadata && Object.keys(metadata).length > 0 ? (
                <JsonViewer value={metadata} />
            ) : (
                <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                    No Metadata Available
                </Typography>
            )}

            <SectionHeading>Assistant Override Variable Values</SectionHeading>
            {overrides && Object.keys(overrides).length > 0 ? (
                <JsonViewer value={overrides} />
            ) : (
                <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                    No overrides set
                </Typography>
            )}
        </Box>
    );
};

export default AnalysisTab;
